import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { Slot } from "radix-ui";
import { cn } from "./utils";
import { INTENT_CLASSES } from "./tokens";

/**
 * Canonical pill shape, shared by every tag-like surface that is NOT itself a
 * `<Badge>` (today: `<SectionTag>`, a raw section-tinted span). It mirrors the
 * `badgeVariants` base exactly — `rounded-full`, `type-subtext`, `font-medium`,
 * `px-2 py-0.5`, a 1px border — so a section pill and an intent/status badge
 * read as the same object. This is the single source of truth for that shape;
 * change it here and every composed tag follows.
 *
 * Shape follows the shadcn radix Badge (https://ui.shadcn.com/docs/components/
 * radix/badge): pill-rounded, sentence/normal case (NOT the old all-caps tag
 * look). The tint + text color still come from the consumer's tokens.
 */
// leading-none pins the pill to one 17px height (11px text + 4px py-0.5 + 2px
// border), converging Badge/Count/COUNT_BUBBLE on a single pill height (PRD R6).
export const TAG_STYLE = "border type-subtext font-medium leading-none px-2 py-0.5 rounded-full";

const badgeVariants = cva(
  // Base = shadcn radix Badge; leading-none drops the ambient 16px line box so
  // the pill is one 17px height (11 text + 4 py + 2 border), PRD R6.
  "inline-flex w-fit shrink-0 items-center justify-center gap-1 overflow-hidden rounded-full border border-transparent px-2 py-0.5 type-subtext font-medium leading-none whitespace-nowrap transition-[color,box-shadow] focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&>svg]:pointer-events-none [&>svg]:size-3",
  {
    variants: {
      variant: {
        // The six shadcn radix standard variants, verbatim.
        default: "bg-primary text-primary-foreground [a&]:hover:bg-primary/90",
        secondary:
          "bg-secondary text-secondary-foreground [a&]:hover:bg-secondary/90",
        destructive:
          "bg-destructive text-white focus-visible:ring-destructive/20 dark:bg-destructive/60 dark:focus-visible:ring-destructive/40 [a&]:hover:bg-destructive/90",
        outline:
          "border-border text-foreground [a&]:hover:bg-accent [a&]:hover:text-accent-foreground",
        ghost: "[a&]:hover:bg-accent [a&]:hover:text-accent-foreground",
        link: "text-primary underline-offset-4 [a&]:hover:underline",
        // Intent variants — tinted backgrounds with semantic text color, the
        // repo's extension on top of the shadcn set. Color is the only thing
        // they add; size/shape/case all come from the base (a tinted intent
        // pill is the same object as a `secondary`/`outline` badge, just
        // colored). Source of truth for color: packages/ui/src/tokens/intents.ts.
        info: cn(INTENT_CLASSES.info.tint, INTENT_CLASSES.info.border),
        success: cn(INTENT_CLASSES.success.tint, INTENT_CLASSES.success.border),
        warning: cn(INTENT_CLASSES.warning.tint, INTENT_CLASSES.warning.border),
        danger: cn(INTENT_CLASSES.danger.tint, INTENT_CLASSES.danger.border),
        neutral: cn(INTENT_CLASSES.neutral.tint, INTENT_CLASSES.neutral.border),
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

function Badge({
  className,
  variant = "default",
  asChild = false,
  ...props
}: React.ComponentProps<"span"> &
  VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot.Root : "span";
  return (
    <Comp
      data-slot="badge"
      data-variant={variant}
      className={cn(badgeVariants({ variant }), className)}
      {...props}
    />
  );
}

export { Badge, badgeVariants };
